/**
 * The viewer's subscription limits, one row per connected subscription — the
 * data behind the board-header strip and the mobile account menu.
 *
 * Founder 2026-10-28 (translated): "all these limits must be shown for every
 * subscription that gets connected". So the rows are the viewer's own
 * CONNECTED subscriptions (every Claude, every ChatGPT/Codex), each named the
 * one way the product names them ("Claude 1", "GPT 2", or the person's own
 * label — @chaos/types `subscriptionInstanceDisplayName`), and the numbers are
 * that subscription's own official-CLI reading from GET /api/account/usage-limits.
 *
 * HONEST STATES, NEVER A FAKE BAR. Each row says which of these it is:
 *   off          the server does not measure limits (SUBSCRIPTION_USAGE_TELEMETRY_ENABLED
 *                is off). Not an error and not zero — not measured.
 *   pending      measuring is on; this subscription has no reading yet.
 *   live         a current reading.
 *   stale        the last good reading is older than two cycles.
 *   unsupported  the vendor's CLI says this login's plan limits cannot be read.
 *   failed       measuring is on and every attempt so far has failed.
 *
 * Sources: GET /api/account/vendors (which subscriptions are connected, their
 * names, and whether this server measures at all — `capabilities`), then, only
 * when it does, GET /api/account/usage-limits. With measuring off the second
 * call is never made: that route answers 404 by design.
 */
import { subscriptionInstanceDisplayName } from '@chaos/types';
import { useQuery } from '@tanstack/react-query';

import { apiJson } from './api/client';
import {
  vendorsApi,
  vendorsKeys,
  type AccountVendorView,
  type SubscriptionUsageInstance as UsageInstanceBase,
  type SubscriptionUsageWindow,
} from './api/vendors';

/** The gateway adds `displayName` (2026-10-28); older gateways omit it. */
type SubscriptionUsageInstance = UsageInstanceBase & { displayName?: string };

export type LimitVendor = 'anthropic' | 'openai';
export type LimitRowState = 'off' | 'pending' | 'live' | 'stale' | 'unsupported' | 'failed';

export interface LimitWindow {
  usedPct: number | null;
  resetsAt: string | null;
}

export interface SubscriptionLimitRow {
  key: string;
  vendor: LimitVendor;
  instanceOrdinal: number;
  /** "Claude 1", "GPT 2", or the person's own name. */
  name: string;
  state: LimitRowState;
  /** Anthropic's 5-hour window. Always null for OpenAI (weekly-only contract). */
  fiveHour: LimitWindow | null;
  /** The binding weekly window: the fuller of weekly / weekly Opus. */
  weekly: LimitWindow | null;
  /** Which weekly window `weekly` is. */
  weeklyScope: 'all' | 'opus' | null;
  fetchedAt: string | null;
}

export interface SubscriptionLimitsView {
  /** `unavailable`: the viewer owns no account here (or the read failed). */
  status: 'loading' | 'unavailable' | 'ready';
  measuring: boolean;
  rows: SubscriptionLimitRow[];
}

const LIMIT_VENDORS: readonly LimitVendor[] = ['anthropic', 'openai'];

interface AccountVendorsBody {
  vendors?: Partial<Record<string, AccountVendorView>>;
  capabilities?: { subscriptionUsageTelemetry?: unknown };
}

function pct(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
}

function windowOf(windows: readonly SubscriptionUsageWindow[], shape: SubscriptionUsageWindow['shape']): LimitWindow | null {
  const found = windows.find((w) => w.shape === shape);
  if (!found) return null;
  const usedPct = pct(found.usedPercent);
  if (usedPct === null && !found.resetsAt) return null;
  return { usedPct, resetsAt: found.resetsAt ?? null };
}

function stateOf(
  usage: SubscriptionUsageInstance | undefined,
  measuring: boolean,
  usageFailed: boolean,
): LimitRowState {
  if (!measuring) return 'off';
  // The limits read itself failed: nothing is known about any row, and
  // "measuring…" would promise a reading that is not coming.
  if (usageFailed) return 'failed';
  if (!usage) return 'pending';
  switch (usage.status) {
    case 'connected':
      return usage.stale ? 'stale' : 'live';
    case 'stale':
      return 'stale';
    case 'unsupported':
      return 'unsupported';
    case 'error':
      return 'failed';
    default:
      return 'pending';
  }
}

/** Pure: the rows the strip renders, from the two owner-only reads. */
export function deriveSubscriptionLimitRows(
  vendors: AccountVendorsBody['vendors'] | undefined,
  usage: readonly SubscriptionUsageInstance[] | null,
  measuring: boolean,
  usageFailed = false,
): SubscriptionLimitRow[] {
  const rows: SubscriptionLimitRow[] = [];
  const seen = new Set<string>();
  const usageByKey = new Map<string, SubscriptionUsageInstance>();
  for (const u of usage ?? []) usageByKey.set(`${u.vendor}:${u.instanceOrdinal}`, u);

  const push = (vendor: LimitVendor, instanceOrdinal: number, label: string | null, displayName?: string) => {
    const key = `${vendor}:${instanceOrdinal}`;
    if (seen.has(key)) return;
    seen.add(key);
    const u = usageByKey.get(key);
    let state = stateOf(u, measuring, usageFailed);
    // Numbers only from a reading; every other state renders no bar at all.
    const windows = u && (state === 'live' || state === 'stale') ? u.windows : [];
    const fiveHour = vendor === 'anthropic' ? windowOf(windows, 'five_hour') : null;
    const weeklyAll = windowOf(windows, 'weekly');
    const weeklyOpus = vendor === 'anthropic' ? windowOf(windows, 'weekly_opus') : null;
    const opusBinds = weeklyOpus !== null && (weeklyAll === null || (weeklyOpus.usedPct ?? -1) > (weeklyAll.usedPct ?? -1));
    const weekly = opusBinds ? weeklyOpus : weeklyAll;
    // A "stale" row with no window was never a reading (e.g. an unsupported
    // login whose next attempt failed): it is a failure, not "last good".
    if ((state === 'live' || state === 'stale') && fiveHour === null && weekly === null) state = 'failed';
    rows.push({
      key,
      vendor,
      instanceOrdinal,
      name: displayName ?? u?.displayName ?? subscriptionInstanceDisplayName(vendor, instanceOrdinal, label ?? u?.label ?? null),
      state,
      fiveHour,
      weekly,
      weeklyScope: weekly ? (opusBinds ? 'opus' : 'all') : null,
      fetchedAt: u?.fetchedAt ?? null,
    });
  };

  for (const vendor of LIMIT_VENDORS) {
    const view = vendors?.[vendor];
    const instances = [...(view?.instances ?? [])].sort((a, b) => a.instanceOrdinal - b.instanceOrdinal);
    for (const i of instances) {
      if (i.status !== 'connected' || i.disabled) continue;
      push(vendor, i.instanceOrdinal, i.label, i.displayName);
    }
  }
  // A measured instance the vendors list did not carry (an older gateway) still shows.
  for (const u of usage ?? []) {
    if (u.vendor === 'anthropic' || u.vendor === 'openai') push(u.vendor, u.instanceOrdinal, u.label);
  }
  return rows;
}

export const subscriptionLimitsKeys = {
  all: ['subscription-limits'] as const,
  vendors: () => [...subscriptionLimitsKeys.all, 'account-vendors'] as const,
};

/**
 * ONE cache entry for the readings (2026-10-28 follow-up). The header strip,
 * the mobile menu, the Settings subscription rows and the Usage & plan list
 * all read `vendorsKeys.usageLimits()` — so a reset, a sign-in or a refetch
 * made by any of them is what every other one shows. Until then the strip
 * kept its own key and could show a pre-reset number for a full cycle after
 * Usage & plan already showed the new one.
 */
export const subscriptionUsageQueryKey = vendorsKeys.usageLimits;

/** The viewer's rows. Refreshes on the gateway's 180 s measuring cadence. */
export function useSubscriptionLimits(): SubscriptionLimitsView {
  const vendorsQ = useQuery({
    queryKey: subscriptionLimitsKeys.vendors(),
    queryFn: () => apiJson<AccountVendorsBody>('/account/vendors'),
    refetchInterval: 180_000,
    staleTime: 60_000,
    retry: false,
  });
  const measuring = vendorsQ.data?.capabilities?.subscriptionUsageTelemetry === true;
  const usageQ = useQuery({
    queryKey: subscriptionUsageQueryKey(),
    queryFn: () => vendorsApi.usageLimits().then((instances) => (instances ?? []) as SubscriptionUsageInstance[]),
    enabled: measuring,
    refetchInterval: 180_000,
    staleTime: 60_000,
    retry: false,
  });

  if (vendorsQ.isError && vendorsQ.data === undefined) return { status: 'unavailable', measuring: false, rows: [] };
  if (vendorsQ.data === undefined) return { status: 'loading', measuring: false, rows: [] };
  return {
    status: 'ready',
    measuring,
    rows: deriveSubscriptionLimitRows(
      vendorsQ.data.vendors,
      measuring ? (usageQ.data ?? null) : null,
      measuring,
      measuring && usageQ.isError && usageQ.data === undefined,
    ),
  };
}
